import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';

import { Button, Dialog, DialogContent, DialogDescription } from '@column/ui';

import { CodeInput, NotificationList } from '~/components';
import { FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';

import { DialogHeaderWithClose } from './DialogHeaderWithClose';

export type AuthenticateModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

export const AuthenticateModal = ({ isOpen, onClose }: AuthenticateModalProps) => {
  const { currentUser, authRequired, refetchCurrentUser, loadSession, setMfaStatus } = useSessionStore();
  const { addSuccessNotification, addDangerNotification, clearNotifications } = useNotificationStore();

  const pendingSmsFactor = currentUser?.factors.find(
    (factor) => !factor.factorVerified && factor.factorType === FactorTypeResponse.SMS
  );
  const [codeInput, setCodeInput] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setCodeInput('');
      clearNotifications('popup');
    }
  }, [isOpen, clearNotifications]);

  const handleResend = () => {
    const { factorId } = pendingSmsFactor || {};
    if (!factorId) {
      return;
    }

    UserRepository.resendFactorVerification({ factorId })
      .then(() => {
        log({
          name: 'User MFA resent',
          context: {
            phoneNumber: currentUser?.phoneNumber,
          },
        });

        addSuccessNotification({
          content: 'Code resent',
          display: 'popup',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  };

  const verifyMfaMutation = useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) =>
      UserRepository.verifyMfa({ factorId, code }),
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to verify. Please try again.',
        display: 'popup',
      });
      setCodeInput('');
    },
    onSuccess: async (resp) => {
      setCodeInput('');
      setMfaStatus(resp.mfaStatus);

      if (authRequired()) {
        try {
          await loadSession();
          await refetchCurrentUser();
          onClose?.();
        } catch (error) {
          addDangerNotification({
            content: error instanceof Error ? error.message : 'An error occurred',
            display: 'popup',
          });
        }
      } else {
        log({
          name: 'User enabled MFA',
          context: currentUser,
        });
        addSuccessNotification({
          content: 'Two-factor authentication enabled',
        });
        await refetchCurrentUser();
        onClose?.();
      }
    },
  });

  const handleSubmit = useCallback(() => {
    if (!currentUser || !pendingSmsFactor?.factorId || verifyMfaMutation.isPending) {
      return;
    }

    verifyMfaMutation.mutate({
      factorId: pendingSmsFactor.factorId,
      code: codeInput,
    });
  }, [currentUser, pendingSmsFactor?.factorId, verifyMfaMutation, codeInput]);

  useEffect(() => {
    if (codeInput && codeInput.length === 6) {
      handleSubmit();
    }
  }, [codeInput, handleSubmit]);

  return (
    <Dialog open={!!isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="gap-3 sm:max-w-sm"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector('input')?.focus();
        }}
      >
        <DialogHeaderWithClose title="Authenticate" />

        <DialogDescription className="pt-3">
          Please enter the 6-digit code we have sent to your phone.
        </DialogDescription>

        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        <CodeInput
          placeholder="######"
          type="number"
          autoComplete="one-time-code"
          name="one-time-code"
          fullWidth
          value={codeInput}
          onChange={(value: string) => setCodeInput(value)}
          isLoading={verifyMfaMutation.isPending}
        />

        <p className="m-0 pt-1 text-center text-xs text-muted-foreground">
          Haven&apos;t received the code?{' '}
          <Button type="button" variant="subtle-primary" className="h-auto p-0 text-xs" onClick={handleResend}>
            Resend
          </Button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
